import * as OTel from '@ms/oteljs';
import { ITelemetryLogger, ILogData, ITelemetryEvent, ITelemetryError } from '../../interfaces/ITelemetryLogger';
import * as OTelTelemetryHelper from './OTelTelemetryHelper';
import { LOG_AUGLOOP_RUNTIME_EVENTS_TO_CONSOLE_BROWSER_ENABLED, IS_DEV_BUILD } from '../../../AppConstants';
import { HostAppSettings, HostAppSettingName } from '../../office-js/HostAppSettings';

/**
 * A shared instance of OTel.TelemetryLogger, which will be used by all instances of OTelTelemetryLogger.
 *
 * Creating an instance of OTel.TelemetryLogger and setting up the same with an OTel.TelemetrySink can be costly,
 * so this shared instance will be created once and used by all the instances of OTelTelemetryLogger.
 *
 * @see OTel.TelemetryLogger
 * @see OTel.TelemetrySink
 */
let telemetryLogger: OTel.TelemetryLogger | null = null;

/**
 * A class to implement ITelemetryLogger interface, using OTel.TelemetryLogger.
 *
 * @see OTel.TelemetryLogger
 * @implements ITelemetryLogger
 */
export class OTelTelemetryLogger implements ITelemetryLogger {
  private logEventsToConsoleBrowserEnabled: boolean;

  /**
   * Default constructor for OTelTelemetryLogger.
   * This takes the responsibility of creating\setting OTel.TelemetryLogger instance only once.
   *
   * @see OTel.TelemetryLogger
   */
  constructor() {
    if (!(telemetryLogger instanceof OTel.TelemetryLogger)) {
      telemetryLogger = OTelTelemetryHelper.createOTelTelemetryLogger();
    }

    this.logEventsToConsoleBrowserEnabled = IS_DEV_BUILD || !HostAppSettings.IsSettingTrue(HostAppSettingName.IsProductionEnvironment);
  }

  /**
   * @implements ITelemetryLogger.LogEvent
   */
  public LogEvent<T extends string>(event: ITelemetryEvent<T>): void {
    if (this.logEventsToConsoleBrowserEnabled) {
      const eventString: string = event.data.reduce<string>((previousValue: string, currentValue: ILogData<T>) => {
        return previousValue + '(' + currentValue.key + ':' + currentValue.value + ')';
        }, 
        'LogEvent: ' + event.eventName + ': ');
        console.log(eventString);
      }

    const dataFields = this.makeOTelDoubleDataField(event.data);
    const telemetryEvent: OTel.TelemetryEvent = OTelTelemetryHelper.createOTelTelemetryEvent(event.eventName, dataFields);

    // Call OTel API to log the OTel.TelemetryEvent.
    telemetryLogger!.sendTelemetryEvent(telemetryEvent);
  }

  /**
   * 
   * @implements ITelemetryLogger.LogError
   */
  public LogError(error: ITelemetryError): void {
    if (this.logEventsToConsoleBrowserEnabled)
      console.error('LogError - area: ' + error.area + ', Message: ' + error.message);

    const dataFields = this.makeOTelDoubleDataField([{ key: "Message", value: error.message}]);
    const errorEvent = OTelTelemetryHelper.createOTelErrorEvent(error.area, dataFields);
    telemetryLogger!.sendError(errorEvent);
  }

  /**
   * This is a direct callback to the OTel API for events originating from the Augloop runtime
   * @param otelEvent Event sent by AL
   */
  public LogEventCallbackForAL(otelEvent: any): void {
    if (LOG_AUGLOOP_RUNTIME_EVENTS_TO_CONSOLE_BROWSER_ENABLED)
      console.log('Augloop Runtime Event: ' + JSON.stringify(otelEvent));

    telemetryLogger!.sendTelemetryEvent(otelEvent);
  }

  /**
   * A utility method to create OTel.DataField based on the data type of value.
   *
   * @param dataKey Key to be used inside DataField.
   * @param dataValue Value to be used inside DataField.
   * @returns An object of OTel.DataField with given Key-Value pair.
   *
   * @see OTel.DataField
   */
  private makeOTelDoubleDataField<T extends string>(data: ILogData<T>[]): OTel.DataField[] {
    return data.map((value: ILogData<T>) : OTel.DataField => {
      if (typeof value.value === 'number') {
        return OTel.makeDoubleDataField(value.key, value.value);
      } else if (typeof value.value === 'string') {
        return OTel.makeStringDataField(value.key, value.value);
      } else if (typeof value.value === 'boolean') {
        return OTel.makeBooleanDataField(value.key, value.value);
      } else {
        return OTel.makeStringDataField(value.key, value.value);
      }
    }, []);
  }
}
